import * as actions from './actions';
import { assembleRows, createCacheKey, generateAsyncStateParams, generateFakeRows } from './utils';
function setDataGridStateFromCache(action, cacheAndKey, sort, pagination, filter) {
  const {
    dispatch
  } = action.payload;
  const {
    pageNumber,
    pageSize,
    asyncState
  } = generateAsyncStateParams(action, sort, pagination, filter);
  const {
    cache,
    cacheKey
  } = cacheAndKey;
  const {
    data,
    totalRecords
  } = cache[cacheKey];
  const {
    allRows,
    loadedRows: visibleRows
  } = assembleRows({
    fetchedRows: data,
    pageNumber,
    pageSize,
    totalRecords
  });
  dispatch({
    type: actions.setDataGridState.type,
    payload: {
      allRows,
      filteredRows: allRows,
      visibleRows,
      ...asyncState,
      totalRecords
    }
  });
}
function makeRequestAndSetDataGridState(action, sort, pagination, filter, cacheAndKey) {
  const {
    dispatch,
    handleApiRequest
  } = action.payload;
  const {
    pageNumber,
    pageSize,
    asyncState
  } = generateAsyncStateParams(action, sort, pagination, filter);
  handleApiRequest(asyncState.sort, {
    pageSize,
    pageNumber
  }, asyncState.filter).then(_ref => {
    let {
      data,
      totalRecords
    } = _ref;
    const {
      allRows,
      loadedRows: visibleRows
    } = assembleRows({
      fetchedRows: data,
      pageNumber,
      pageSize,
      totalRecords
    });
    const {
      cache,
      cacheKey
    } = cacheAndKey;
    dispatch({
      type: actions.setDataGridState.type,
      payload: {
        allRows,
        filteredRows: allRows,
        visibleRows,
        ...asyncState,
        totalRecords,
        cache: {
          ...cache,
          [cacheKey]: {
            data: visibleRows,
            totalRecords
          }
        }
      }
    });
  });
}
export default function asyncDataGridReducer(state, action) {
  const {
    totalRecords,
    sort,
    pagination,
    filter,
    cache
  } = state;
  // eslint-disable-next-line
  const {
    dispatch,
    transformFn,
    defaultSort,
    handleApiRequest,
    ...payload
  } = action.payload;
  switch (action.type) {
    case actions.setDataGridState.type:
      {
        return {
          ...state,
          ...payload
        };
      }
    case actions.setSort.type:
      {
        const cacheKey = createCacheKey(payload, {
          pageSize: pagination.pageSize,
          pageNumber: 1
        }, filter);
        if (cache[cacheKey] !== undefined) {
          setDataGridStateFromCache(action, {
            cache,
            cacheKey
          }, sort, pagination, filter);
          return {
            ...state
          };
        }

        // Set loading state with fake rows
        generateFakeRows(dispatch, transformFn, totalRecords, pagination.pageSize);
        makeRequestAndSetDataGridState(action, sort, pagination, filter, {
          cache,
          cacheKey
        });
        return {
          ...state
        };
      }
    case actions.initPagination.type:
      {
        return {
          ...state,
          pagination: {
            ...payload
          }
        };
      }
    case actions.resetPagination.type:
      {
        return {
          ...state,
          totalRecords: 0,
          allRows: [],
          visibleRows: [],
          filteredRows: [],
          sort: defaultSort,
          pagination: {
            pageSize: 10,
            currentPage: 0,
            loading: true
          }
        };
      }
    case actions.goToPage.type:
      {
        const {
          payload: pageIndex
        } = payload;
        const cacheKey = createCacheKey(sort, {
          pageSize: pagination.pageSize,
          pageNumber: pageIndex + 1
        }, filter);
        if (cache[cacheKey] !== undefined) {
          setDataGridStateFromCache(action, {
            cache,
            cacheKey
          }, sort, pagination, filter);
          return {
            ...state
          };
        }

        // sets loading state by faking rows
        generateFakeRows(dispatch, transformFn, totalRecords, pagination.pageSize);
        makeRequestAndSetDataGridState(action, sort, pagination, filter, {
          cache,
          cacheKey
        });
        return {
          ...state
        };
      }
    case actions.setPageSize.type:
      {
        const {
          payload: updatedPageSize
        } = payload;
        const cacheKey = createCacheKey(sort, {
          pageSize: updatedPageSize,
          pageNumber: 1
        }, filter);
        if (cache[cacheKey] !== undefined) {
          setDataGridStateFromCache(action, {
            cache,
            cacheKey
          }, sort, pagination, filter);
          return {
            ...state
          };
        }

        // sets loading state by faking rows
        generateFakeRows(dispatch, transformFn, totalRecords, pagination.pageSize);
        makeRequestAndSetDataGridState(action, sort, pagination, filter, {
          cache,
          cacheKey
        });
        return {
          ...state
        };
      }
    case actions.registerFilter.type:
      {
        // eslint-disable-next-line
        const {
          key: filterKey,
          handleApiRequest,
          ...rest
        } = payload;
        return {
          ...state,
          filter: {
            ...filter,
            [filterKey]: {
              ...rest,
              key: filterKey,
              value: getInitialFilterValue(state, payload)
            }
          }
        };
      }
    case actions.updateFilter.type:
      {
        // eslint-disable-next-line
        const {
          key: filterKey,
          value: filterValue,
          handleApiRequest
        } = payload;
        const newFilterState = {
          ...filter,
          [filterKey]: {
            ...filter[filterKey],
            key: filterKey,
            value: filterValue
          }
        };
        const cacheKey = createCacheKey(sort, {
          pageSize: pagination.pageSize,
          pageNumber: 1
        }, newFilterState);
        if (cache[cacheKey] !== undefined) {
          setDataGridStateFromCache(action, {
            cache,
            cacheKey
          }, sort, pagination, filter);
          return {
            ...state
          };
        }
        generateFakeRows(dispatch, transformFn, totalRecords, pagination.pageSize);
        makeRequestAndSetDataGridState(action, sort, pagination, filter, {
          cache,
          cacheKey
        });
        return {
          ...state
        };
      }
    default:
      {
        return {
          ...state
        };
      }
  }
  function getInitialFilterValue(prevState, payload) {
    const {
      key,
      value,
      defaultValue
    } = payload;
    if (prevState && prevState.filter[key]) {
      return prevState.filter[key].value;
    }
    return value ? value : defaultValue;
  }
}